/*
 * Page metadata
 */

import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import StripTags from 'striptags';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Meta
 * @props (see propTypes)
 */
const Meta = (props) => {
    const page = props.page || {};

    // Meta
    const description =
        props.description ||
        page.searchDescription ||
        page.standfirst ||
        page.description ||
        '';
    const image =
        props.image || page.metaImage || page.listingImage || page.heroImage;
    const keywords = props.keywords || page.metaKeywords;
    const title = props.title || page.seoTitle || page.title || 'Phoenix Court';
    const url = props.url
        ? `${props.domain}/${props.url}`
        : page.url
            ? `${props.domain}${page.url}`
            : props.domain;
    const twitterHandle = null;

    const siteName = 'Phoenix Court';

    const ldJson = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                '@id': props.domain,
                url: props.domain,
                name: title,
                isPartOf: {
                    '@id': `${props.domain}#website`,
                },
                about: {
                    '@id': `${props.domain}#organization`,
                },
                description,
            },
            {
                '@type': 'WebSite',
                '@id': `${props.domain}#website`,
                url: props.domain,
                name: siteName,
                publisher: {
                    '@id': `${props.domain}#organization`,
                },
            },
            {
                '@type': 'Organization',
                '@id': `${props.domain}#organization`,
                name: siteName,
                alternateName: 'Phoenix Court Group',
                url: props.domain,
                logo: {
                    '@type': 'ImageObject',
                    '@id': `${props.domain}/#/schema/logo/image/`,
                    url: `${props.domain}/images/logo.png`,
                    contentUrl: `${props.domain}/images/logo.png`,
                    width: 512,
                    height: 512,
                    caption: siteName,
                },
                image: {
                    '@id': `${props.domain}/#/schema/logo/image/`,
                },
            },
        ],
    };

    return (
        <Helmet>
            <meta
                content={siteName}
                name='site_name' />
            <meta
                content={siteName}
                property='og:site_name' />
            <script type='application/ld+json'>{JSON.stringify(ldJson)}</script>
            {title && <title>{title}</title>}
            {description && (
                <meta
                    content={StripTags(description)}
                    name='description' />
            )}
            {keywords && <meta
                content={keywords}
                name='keywords' />}
            {description && (
                <meta
                    content={StripTags(description)}
                    property='og:description'/>
            )}
            <meta
                content={
                    image ? image.src : `${props.domain}/images/social.jpg`
                }
                property='og:image'/>
            {title && <meta
                content={title}
                property='og:title' />}
            {(props.canonicalUrl || url) && (
                <meta
                    content={props.canonicalUrl || url}
                    property='og:url' />
            )}
            <meta
                content='website'
                property='og:type' />
            <meta
                content='summary_large_image'
                name='twitter:card' />
            {twitterHandle && (
                <meta
                    content={twitterHandle}
                    name='twitter:creator' />
            )}
            {description && (
                <meta
                    content={StripTags(description)}
                    name='twitter:description'/>
            )}
            <meta
                content={
                    image ? image.src : `${props.domain}/images/social.jpg`
                }
                property='twitter:image:src'/>
            {title && <meta
                content={title}
                property='twitter:title' />}

            {/* Canonical URL */}
            {props.canonicalUrl && (
                <link
                    href={props.canonicalUrl}
                    rel='canonical' />
            )}
        </Helmet>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Meta.propTypes = {
    description: PropTypes.string,
    domain: PropTypes.string,
    image: PropTypes.object,
    keywords: PropTypes.string,
    page: PropTypes.object,
    title: PropTypes.string,
    url: PropTypes.string,
    canonicalUrl: PropTypes.string,
};

export default Meta;
